import { memoizeFunction } from '@fluentui/react';
import { ITheme } from '@fluentui/react/lib/Styling';

import {
  AppColors,
  appNeutralColors,
  ButtonSemanticColors,
  defaultAppColorPalette,
  getModernUxExperimentPreference,
} from '@ms/acui-common';
import { AppTheme, AppThemeManager, isSharedUXDarkModeOn, isThemingEnabled } from '@ms/acui-theme';

import { Variant } from './AppButtonVariants';

/*
  Themed buttons are buttons that get colors automatically set based on the chosen variants.
  @see AppButtonVariants
*/

/* Theming */
const defaultVariantButtonColors = {
  defaultBackgroundColor: defaultAppColorPalette.white,
  hoverBackgroundColor: defaultAppColorPalette.gray30,
  pressedBackgroundColor: defaultAppColorPalette.gray50,
  toggledBackgroundColor: defaultAppColorPalette.gray40,
  disabledBackgroundColor: defaultAppColorPalette.gray20,
  focusedBackgroundColor: 'transparent',
  textColor: defaultAppColorPalette.primary,
  textHoverColor: defaultAppColorPalette.shade10,
  textPressedColor: defaultAppColorPalette.shade20,
  textToggledColor: defaultAppColorPalette.shade10,
  textDisabledColor: defaultAppColorPalette.gray70,
  disabledBorderColor: defaultAppColorPalette.gray50,
  focusedBorderColor: defaultAppColorPalette.black,
  focusDisabledBackgroundColor: 'transparent',
  borderColor: defaultAppColorPalette.gray60,
  borderHoverColor: defaultAppColorPalette.gray60,
  borderPressedColor: defaultAppColorPalette.gray60,
  borderToggledColor: defaultAppColorPalette.gray60,
};

const primaryVariantButtonColorsWithTheme = memoizeFunction(
  (theme: ITheme): ButtonSemanticColors => {
    const lightTheme = AppThemeManager.getInstance().getThemeForMode(AppTheme.Light);
    return {
      defaultBackgroundColor: theme.palette.themePrimary,
      hoverBackgroundColor: theme.palette.themeDarkAlt,
      pressedBackgroundColor: theme.palette.themeDarker,
      toggledBackgroundColor: theme.palette.themeDark,
      disabledBackgroundColor: theme.palette.neutralLighterAlt,
      focusedBackgroundColor: 'transparent',
      textColor: lightTheme.palette.white,
      textHoverColor: lightTheme.palette.white,
      textPressedColor: lightTheme.palette.white,
      textToggledColor: lightTheme.palette.white,
      textDisabledColor: theme.palette.neutralTertiaryAlt,
      disabledBorderColor: theme.palette.neutralQuaternaryAlt,
      focusedBorderColor: theme.palette.black,
      focusDisabledBackgroundColor: 'transparent',
      borderColor: theme.palette.themePrimary,
      borderHoverColor: theme.palette.themeDarkAlt,
      borderPressedColor: theme.palette.themeDarker,
      borderToggledColor: theme.palette.themeDark,
    };
  }
);

const invertedVariantButtonColorsWithTheme = memoizeFunction(
  (theme: ITheme): ButtonSemanticColors => {
    const invertedVariantButtonSemanticColors = {
      defaultBackgroundColor: theme.palette.white,
      hoverBackgroundColor: theme.palette.neutralLighter,
      pressedBackgroundColor: theme.palette.neutralQuaternaryAlt,
      toggledBackgroundColor: theme.palette.neutralLight,
      disabledBackgroundColor: theme.palette.neutralLighterAlt,
      focusedBackgroundColor: 'transparent',
      textColor: theme.palette.themePrimary,
      textHoverColor: theme.palette.themeDarkAlt,
      textPressedColor: theme.palette.themeDark,
      textToggledColor: theme.palette.themeDarkAlt,
      textDisabledColor: theme.palette.neutralTertiaryAlt,
      disabledBorderColor: theme.palette.neutralQuaternaryAlt,
      focusedBorderColor: theme.palette.black,
      focusDisabledBackgroundColor: 'transparent',
      borderColor: theme.palette.neutralQuaternary,
      borderHoverColor: theme.palette.neutralQuaternary,
      borderPressedColor: theme.palette.neutralQuaternary,
      borderToggledColor: theme.palette.neutralQuaternary,
    };
    if (!isSharedUXDarkModeOn()) {
      return invertedVariantButtonSemanticColors;
    }
    return {
      ...invertedVariantButtonSemanticColors,
      defaultBackgroundColor: theme.palette.neutralLighterAlt,
      hoverBackgroundColor: theme.palette.neutralLighter,
      pressedBackgroundColor: theme.palette.neutralQuaternaryAlt,
      toggledBackgroundColor: theme.palette.neutralLight,
      disabledBackgroundColor: theme.palette.neutralLighterAlt,
      textColor: theme.palette.neutralDark,
      textDisabledColor: theme.palette.neutralTertiaryAlt,
    };
  }
);

// Modern ux uses: fluentUiTheme
const modernUxInvertedVariantButtonColorsWithTheme = memoizeFunction(
  (theme: ITheme): ButtonSemanticColors => {
    return {
      defaultBackgroundColor: theme.palette.white,
      hoverBackgroundColor: theme.palette.neutralLighter,
      pressedBackgroundColor: theme.palette.neutralTertiaryAlt,
      toggledBackgroundColor: theme.palette.neutralQuaternary,
      disabledBackgroundColor: theme.palette.white,
      focusedBackgroundColor: 'transparent',
      textColor: theme.palette.themePrimary,
      textHoverColor: theme.palette.themePrimary,
      textPressedColor: theme.palette.themePrimary,
      textToggledColor: theme.palette.themePrimary,
      textDisabledColor: theme.palette.themePrimary,
      disabledBorderColor: theme.palette.neutralTertiary,
      focusedBorderColor: theme.palette.neutralPrimaryAlt,
      focusDisabledBackgroundColor: 'transparent',
      borderColor: theme.palette.neutralTertiary,
      borderHoverColor: theme.palette.neutralTertiary,
      borderPressedColor: theme.palette.neutralTertiary,
      borderToggledColor: theme.palette.neutralTertiary,
    };
  }
);

const getInvertedVariantButtonColorsWithTheme = (theme: ITheme): ButtonSemanticColors => {
  if (getModernUxExperimentPreference()) {
    return modernUxInvertedVariantButtonColorsWithTheme(theme);
  }
  return invertedVariantButtonColorsWithTheme(theme);
};

const getAppThemedButtonColorsWithTheme = (variant: Variant, theme: ITheme): ButtonSemanticColors => {
  switch (variant) {
    case Variant.primary:
      return primaryVariantButtonColorsWithTheme(theme);
    case Variant.inverted:
      return getInvertedVariantButtonColorsWithTheme(theme);
    default:
      return defaultVariantButtonColors;
  }
};

/* App colors - these will only be used when theming is not enabled */
const primaryVariantButtonColorsWithAppColors = memoizeFunction(
  (appColors: AppColors): ButtonSemanticColors => {
    return {
      defaultBackgroundColor: appColors.primary,
      hoverBackgroundColor: appColors.shade10,
      pressedBackgroundColor: appColors.shade30,
      toggledBackgroundColor: appColors.shade20,
      disabledBackgroundColor: appNeutralColors.gray20,
      focusedBackgroundColor: 'transparent',
      textColor: appNeutralColors.white,
      textHoverColor: appNeutralColors.white,
      textPressedColor: appNeutralColors.white,
      textToggledColor: appNeutralColors.white,
      textDisabledColor: appNeutralColors.gray70,
      disabledBorderColor: appNeutralColors.gray50,
      focusedBorderColor: appNeutralColors.black,
      focusDisabledBackgroundColor: 'transparent',
      borderColor: appColors.primary,
      borderHoverColor: appColors.shade10,
      borderPressedColor: appColors.shade30,
      borderToggledColor: appColors.shade20,
    };
  }
);

const invertedVariantButtonColorsWithAppColors = memoizeFunction(
  (appColors: AppColors): ButtonSemanticColors => {
    return {
      defaultBackgroundColor: appNeutralColors.white,
      hoverBackgroundColor: appNeutralColors.gray30,
      pressedBackgroundColor: appNeutralColors.gray50,
      toggledBackgroundColor: appNeutralColors.gray40,
      disabledBackgroundColor: appNeutralColors.gray20,
      focusedBackgroundColor: 'transparent',
      textColor: appColors.primary,
      textHoverColor: appColors.shade10,
      textPressedColor: appColors.shade20,
      textToggledColor: appColors.shade10,
      textDisabledColor: appNeutralColors.gray70,
      disabledBorderColor: appNeutralColors.gray50,
      focusedBorderColor: appNeutralColors.black,
      focusDisabledBackgroundColor: 'transparent',
      borderColor: appNeutralColors.gray60,
      borderHoverColor: appNeutralColors.gray60,
      borderPressedColor: appNeutralColors.gray60,
      borderToggledColor: appNeutralColors.gray60,
    };
  }
);

// Modern ux uses fluentUiTheme
const modernUxInvertedVariantButtonColorsWithAppColors = memoizeFunction(
  (appColors: AppColors): ButtonSemanticColors => {
    return {
      defaultBackgroundColor: appNeutralColors.white,
      hoverBackgroundColor: '#F5F5F5',
      pressedBackgroundColor: '#E0E0E0',
      toggledBackgroundColor: '#EBEBEB',
      disabledBackgroundColor: appNeutralColors.white,
      focusedBackgroundColor: 'transparent',
      textColor: appColors.primary,
      textHoverColor: appColors.primary,
      textPressedColor: appColors.primary,
      textToggledColor: appColors.primary,
      textDisabledColor: appColors.primary,
      disabledBorderColor: '#D1D1D1',
      focusedBorderColor: '#616161',
      focusDisabledBackgroundColor: 'transparent',
      borderColor: '#D1D1D1',
      borderHoverColor: '#D1D1D1',
      borderPressedColor: '#D1D1D1',
      borderToggledColor: '#D1D1D1',
    };
  }
);

const getInvertedVariantButtonColorsWithAppColors = (appColors: AppColors): ButtonSemanticColors => {
  if (getModernUxExperimentPreference()) {
    return modernUxInvertedVariantButtonColorsWithAppColors(appColors);
  }
  return invertedVariantButtonColorsWithAppColors(appColors);
};

const getAppThemedButtonColorsWithAppColors = (variant: Variant, appColors: AppColors): ButtonSemanticColors => {
  switch (variant) {
    case Variant.primary:
      return primaryVariantButtonColorsWithAppColors(appColors);
    case Variant.inverted:
      return getInvertedVariantButtonColorsWithAppColors(appColors);
    default:
      return defaultVariantButtonColors;
  }
};

export const getAppThemedButtonColors = (
  variant: Variant,
  theme: ITheme,
  appColors: AppColors
): ButtonSemanticColors => {
  // first try using themes
  if (isThemingEnabled()) {
    return getAppThemedButtonColorsWithTheme(variant, theme);
  }
  // fall back to app colors if theming is not enabled
  return getAppThemedButtonColorsWithAppColors(variant, appColors);
};
